import {
  ArrowLeftIcon,
  BellRingingIcon,
  CalendarBlankIcon,
  CaretRightIcon,
  CheckCircleIcon,
  SlidersHorizontalIcon,
  TrophyIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react'
import { type PlainDate, differenceInDays, formatMoney } from '@paybae/shared'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { ErrorState, Skeleton } from '@/components/Estado'
import { DetalleMovimiento } from '@/components/DetalleMovimiento'
import { PersonAvatar } from '@/components/PersonAvatar'
import { ScreenHeader } from '@/components/ScreenHeader'
import type { ActividadBandeja, AvisoBandeja } from '@/lib/api'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'
import {
  useAvisos,
  useCards,
  useDashboard,
  useMarcarAvisosVistos,
  useRecordatorios,
} from '@/lib/queries'

/**
 * La bandeja detrás de la campanita.
 *
 * TESIS
 *
 * Un aviso sin salida se ignora. Por eso la pantalla se parte en dos grupos que
 * responden a preguntas distintas: **«Te toca hacer algo»**, donde cada renglón
 * lleva el botón que lleva al sitio exacto donde se resuelve, y **«Lo que
 * pasó»**, que solo informa y no pide nada. Mezclarlos —que es lo que hace un
 * feed cronológico— obliga a releer cada línea para decidir si exige algo.
 *
 * POR QUÉ NO TODO EN ROJO
 *
 * Lo vencido se distingue por **posición y peso**: va primero y en negrita, y
 * solo ahí aparece el coral. Si cada renglón gritara, el grito dejaría de
 * significar «esto ya se te pasó» en tres días, que es exactamente cómo mueren
 * las bandejas de notificaciones.
 *
 * LO QUE SE CONGELA AL ABRIR
 *
 * Entrar marca la bandeja como vista, y eso apaga el `nuevo` de todos los
 * renglones en el servidor. Si la pantalla se dibujara con lo que responde la
 * API después de esa marca, los distintivos de «nuevo» desaparecerían delante
 * de los ojos de quien acaba de abrirla. Se guarda una foto de lo que estaba
 * sin ver en la primera lectura y se pinta con esa, hasta salir de la pantalla.
 */
export function Avisos() {
  const navigate = useNavigate()
  const { data, isPending, isError, error, refetch } = useAvisos()
  const marcarVistos = useMarcarAvisosVistos()

  /**
   * Foto de lo que estaba sin ver al abrir. `null` mientras no ha habido
   * primera lectura; a partir de ahí no vuelve a cambiar aunque el servidor
   * diga que ya no hay nada nuevo —que es justo lo que dirá tras el acuse.
   */
  const [nuevos, setNuevos] = useState<ReadonlySet<string> | null>(null)
  const [detalle, setDetalle] = useState<string | null>(null)

  useEffect(() => {
    if (!data || nuevos !== null) return

    const marcados = new Set<string>()
    // La primera apertura no tiene «desde la última vez»: el servidor marca
    // como nuevo absolutamente todo, y cuarenta renglones gritando NUEVO no
    // señalan nada. Se estrena en limpio y a partir de aquí sí hay contra qué
    // comparar.
    if (data.vistoEn !== null) {
      for (const a of data.avisos) if (a.nuevo) marcados.add(a.id)
      for (const a of data.actividad) if (a.nuevo) marcados.add(a.id)
    }
    setNuevos(marcados)

    // `hasta` es este instante, no el del servidor: es el momento en que esta
    // pantalla tuvo los datos delante. Lo que entre después seguirá contando
    // como sin ver, que es lo correcto — nadie lo ha leído.
    marcarVistos.mutate(new Date().toISOString())
    // `marcarVistos` cambia de identidad en cada render de react-query; incluirlo
    // dispararía el acuse en bucle. El candado real es `nuevos !== null`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, nuevos])

  if (isPending) return <Cargando />

  if (isError || !data) {
    return (
      <>
        <Encabezado onVolver={() => navigate('/')} onAjustes={() => navigate('/avisos/ajustes')} />
        <ErrorState
          titulo="No pudimos cargar tus avisos"
          mensaje={error instanceof Error ? error.message : 'Inténtalo de nuevo.'}
          onReintentar={() => void refetch()}
        />
      </>
    )
  }

  const pendientes = data.avisos.filter((a) => a.accion !== null)
  const informativos = data.avisos.filter((a) => a.accion === null)
  const esNuevo = (id: string): boolean => nuevos?.has(id) ?? false
  const nuevasEnActividad = data.actividad.filter((f) => esNuevo(f.id)).length

  return (
    <div data-pantalla="avisos">
      <style>{CSS_AVISOS}</style>

      <Encabezado
        onVolver={() => navigate('/')}
        onAjustes={() => navigate('/avisos/ajustes')}
        subtitulo={
          pendientes.length === 0
            ? 'Nada pendiente'
            : `${pendientes.length} ${pendientes.length === 1 ? 'cosa por hacer' : 'cosas por hacer'}`
        }
      />

      <div className="px-safe mx-auto max-w-lg pb-4">
        <Seccion numero="01" titulo="Pendientes">
          {pendientes.length === 0 ? (
            <NadaPendiente hoy={data.today} />
          ) : (
            <ul className="av-filas">
              {pendientes.map((aviso) => (
                <FilaPendiente
                  key={aviso.id}
                  aviso={aviso}
                  hoy={data.today}
                  nuevo={esNuevo(aviso.id)}
                  onIr={() => {
                    haptic('light')
                    navigate(aviso.accion!.ruta)
                  }}
                />
              ))}
            </ul>
          )}
        </Seccion>

        {/* La cuenta de nuevas va en el encabezado: es lo que deja decidir si
            vale la pena recorrer la lista antes de recorrerla. */}
        <Seccion
          numero="02"
          titulo="Actividad"
          extra={
            nuevasEnActividad > 0
              ? `${nuevasEnActividad} ${nuevasEnActividad === 1 ? 'nueva' : 'nuevas'}`
              : undefined
          }
        >
          {informativos.length === 0 && data.actividad.length === 0 ? (
            <p className="py-6 text-[13px] leading-snug text-ink-soft">
              Todavía no hay movimientos que contar. En cuanto alguno de los dos registre un gasto o
              confirme un ingreso, aparecerá aquí.
            </p>
          ) : (
            <>
              {informativos.length > 0 ? (
                <ul className="av-filas mb-1">
                  {informativos.map((aviso) => (
                    <FilaInformativa key={aviso.id} aviso={aviso} nuevo={esNuevo(aviso.id)} />
                  ))}
                </ul>
              ) : null}

              {agruparPorDia(data.actividad, data.today).map((grupo) => (
                <section key={grupo.dia} className="pt-1">
                  {/* El rótulo del día no se queda pegado al desplazar: el
                      encabezado de la pantalla ya ocupa la franja superior y su
                      alto cambia con el subtítulo, así que cualquier `top` fijo
                      lo escondería detrás justo en la mitad de los casos. */}
                  <p className="av-fechado py-2">{grupo.etiqueta}</p>
                  <ul className="av-filas">
                    {grupo.filas.map((fila) => (
                      <FilaActividad
                        key={fila.id}
                        fila={fila}
                        nuevo={esNuevo(fila.id)}
                        // Solo lleva a algún sitio si el renglón habla de un
                        // movimiento: un aviso de «se creó una invitación» no
                        // tiene ficha que abrir.
                        onAbrir={
                          fila.entityType === 'transaction'
                            ? () => setDetalle(fila.entityId)
                            : undefined
                        }
                      />
                    ))}
                  </ul>
                </section>
              ))}
            </>
          )}
        </Seccion>

        <Seccion numero="03" titulo="Cómo te avisamos">
          <AccesosConfiguracion />
        </Seccion>
      </div>

      <DetalleMovimiento id={detalle} onCerrar={() => setDetalle(null)} />
    </div>
  )
}

/* ── Renglones ──────────────────────────────────────────────────────────── */

/**
 * Un renglón que pide una acción.
 *
 * La fila entera es el botón y la píldora dice qué va a pasar al tocarla. Se
 * prefiere eso a una píldora táctil dentro de una fila inerte porque en 375 px
 * el blanco útil de la píldora sola sería de unos 110 px de ancho: fácil de
 * fallar con el pulgar, y fallar aquí significa no hacer el pago.
 *
 * La píldora va en su propio renglón y no a la derecha del texto. Los títulos
 * del servidor traen el plazo dentro —«QA Predial venció hace 5 días»— y en
 * 375 px eso son dos o tres líneas: con la píldora al costado, el texto se
 * queda en una columna de 200 px y la píldora flota a media altura, sin
 * alinearse con nada.
 */
function FilaPendiente({
  aviso,
  hoy,
  nuevo,
  onIr,
}: {
  aviso: AvisoBandeja
  hoy: PlainDate
  nuevo: boolean
  onIr: () => void
}) {
  const vencido = aviso.urgencia === 'VENCIDO'

  return (
    <li>
      <button
        type="button"
        onClick={onIr}
        aria-label={`${aviso.accion!.etiqueta}: ${aviso.titulo}`}
        className="flex w-full flex-col items-stretch gap-1 py-3.5 text-left active:opacity-70"
      >
        <span
          className={cn(
            'av-versalita flex items-center gap-1',
            vencido ? 'text-expense-text' : aviso.urgencia === 'HOY' ? 'text-ink' : 'text-ink-faint',
          )}
        >
          {vencido ? <WarningCircleIcon size={12} weight="fill" className="shrink-0" /> : null}
          {cuando(aviso, hoy)}
          {nuevo ? <span className="av-nuevo">nuevo</span> : null}
        </span>

        <span
          className={cn(
            'block text-[15px] leading-snug text-ink',
            // El peso es lo que separa lo vencido de lo que solo viene, junto
            // con su posición: la negrita no se reparte, o deja de destacar.
            vencido ? 'font-bold' : 'font-semibold',
          )}
          data-money
        >
          {aviso.titulo}
        </span>

        {aviso.detalle ? (
          <span data-money className="block text-[13px] leading-snug text-ink-soft">
            {aviso.detalle}
          </span>
        ) : null}

        <span className="mt-1.5 flex">
          {/* Pastilla de contorno en tinta, la misma de «Registrar pago» y
              «Ya pagué». Era índigo suave, y en esta paleta el índigo es del
              dato y de los enlaces: una acción que cambia algo —marcar un pago—
              tiene que verse como botón, no como vínculo. */}
          <span
            className={cn(
              'flex min-h-[38px] items-center gap-0.5 rounded-pill border border-line-strong bg-surface px-3.5',
              'text-[13px] font-bold text-ink',
            )}
          >
            {aviso.accion!.etiqueta}
            <CaretRightIcon size={13} weight="bold" />
          </span>
        </span>
      </button>
    </li>
  )
}

/** Un aviso que no pide nada: una meta alcanzada solo se celebra. */
function FilaInformativa({ aviso, nuevo }: { aviso: AvisoBandeja; nuevo: boolean }) {
  const Icono = aviso.tipo === 'META_ALCANZADA' ? TrophyIcon : CheckCircleIcon

  return (
    <li className="flex items-start gap-3 py-3">
      <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-income-soft text-income-text">
        <Icono size={15} weight="fill" />
      </span>
      <span className="min-w-0 flex-1">
        {/* El título no se trunca: el nombre de la meta es justo lo que se
            quiere leer, y «Llegaste a la meta de QA-UI Viaje a …» corta
            precisamente donde empieza la noticia. */}
        <span data-money className="text-[14px] font-semibold leading-snug text-ink">
          {aviso.titulo}
          {nuevo ? <span className="av-nuevo ml-1.5 align-middle">nuevo</span> : null}
        </span>
        {aviso.detalle ? (
          <span data-money className="mt-0.5 block text-[12.5px] leading-snug text-ink-soft">
            {aviso.detalle}
          </span>
        ) : null}
      </span>
    </li>
  )
}

/**
 * Una línea del feed de actividad.
 *
 * El punto de color va antes que el nombre porque es lo que responde «¿quién
 * fue?» sin leer: en un hogar de dos, el color identifica más rápido que el
 * nombre propio, y el nombre queda como respaldo para quien no distingue tonos.
 */
/**
 * Una línea de actividad.
 *
 * Lo nuevo se distingue por el **fondo**, no por el tono del texto. Antes la
 * única diferencia era `text-ink` contra `text-ink-soft`, y eso es invisible
 * mientras se recorre una lista. Peor: el punto de color de la izquierda —que
 * dice de quién es— se leía como marca de no leído, así que todo parecía nuevo.
 *
 * El punto se queda en su sitio y sigue significando lo mismo. El fondo es una
 * señal distinta, en otro plano, y no compite con él.
 */
function FilaActividad({
  fila,
  nuevo,
  onAbrir,
}: {
  fila: ActividadBandeja
  nuevo: boolean
  onAbrir?: () => void
}) {
  // Un punto de color obliga a recordar de quién es cada tono. Con dos personas
  // se aprende; con cuatro, no. Las iniciales dentro del color dicen las dos
  // cosas a la vez y no hay nada que memorizar.
  const contenido = (
    <>
      <PersonAvatar
        name={fila.autor}
        token={fila.personColorToken}
        size="sm"
        className="mt-[1px] shrink-0"
      />
      <span className="min-w-0 flex-1">
        <span
          data-money
          className={cn('text-[13.5px] leading-snug', nuevo ? 'text-ink' : 'text-ink-soft')}
        >
          <span className="font-semibold text-ink">{fila.autor}</span> {minuscula(fila.resumen)}
        </span>
      </span>
      <span className="av-fechado shrink-0">{hora(fila.createdAt)}</span>
    </>
  )

  const clases = cn(
    // El margen negativo compensa el relleno: el bloque teñido sobresale hasta
    // el borde de la retícula sin desalinear el texto con el resto. El ancho
    // crece lo mismo que se sale; con `w-full` solo se desplazaba, y el bloque
    // quedaba a 6 px del borde izquierdo y a 26 px del derecho.
    'flex w-[calc(100%+1.25rem)] items-start gap-2.5 rounded-chip px-2.5 py-2.5 text-left',
    '-mx-2.5',
    nuevo && 'bg-primary-soft',
  )

  return (
    <li>
      {onAbrir ? (
        <button type="button" onClick={onAbrir} className={cn(clases, 'active:opacity-70')}>
          {contenido}
        </button>
      ) : (
        <div className={clases}>{contenido}</div>
      )}
    </li>
  )
}

/* ── Vacío ───────────────────────────────────────────────────────────────── */

/**
 * Nada pendiente.
 *
 * Un hueco con «no hay datos» deja la duda de si la pantalla falló. Aquí se
 * dice lo próximo que viene —la nómina o el corte más cercano— para que la
 * lectura termine con una fecha en la cabeza y no con un vacío.
 */
function NadaPendiente({ hoy }: { hoy: PlainDate }) {
  const { data: tablero } = useDashboard()
  const { data: tarjetas } = useCards()

  const proximo = useMemo(() => {
    const candidatos: Array<{ fecha: PlainDate; texto: string; montoCents: number | null }> = []

    // `upcomingIncomes` viene ordenado por fecha y llega más allá del periodo:
    // basta el primero para saber cuándo entra dinero otra vez.
    const ingreso = tablero?.upcomingIncomes?.[0]
    if (ingreso) {
      candidatos.push({
        fecha: ingreso.date,
        texto: ingreso.name,
        montoCents: ingreso.amountCents,
      })
    }

    for (const tarjeta of tarjetas ?? []) {
      // El corte ya cerrado es el que se paga; el abierto todavía admite
      // compras y su fecha límite no es una obligación de este periodo.
      const estado = tarjeta.closedStatement
      if (!estado || estado.balanceDueCents <= 0) continue
      candidatos.push({
        fecha: estado.dueDate,
        texto: `Pago de ${tarjeta.name}`,
        montoCents: estado.balanceDueCents,
      })
    }

    if (candidatos.length === 0) return null
    return candidatos.sort((a, b) => a.fecha.localeCompare(b.fecha))[0]!
  }, [tablero, tarjetas])

  const dias = proximo ? differenceInDays(proximo.fecha, hoy) : 0

  return (
    <div className="flex items-start gap-3.5 py-4">
      <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-income-soft text-income-text">
        <CheckCircleIcon size={22} weight="fill" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-display text-[17px] font-bold leading-snug tracking-[-0.03em] text-ink">
          Nada pendiente
        </p>
        {proximo ? (
          <p className="mt-1 text-[13px] leading-snug text-ink-soft">
            Lo próximo es{' '}
            <span className="font-semibold text-ink">{proximo.texto}</span>
            {proximo.montoCents !== null ? (
              <>
                ,{' '}
                <span data-money>{formatMoney(proximo.montoCents, { hideZeroCents: true })}</span>
              </>
            ) : null}
            , el {fechaLarga(proximo.fecha)}
            {dias > 0 ? ` (en ${dias} ${dias === 1 ? 'día' : 'días'})` : ''}.
          </p>
        ) : (
          <p className="mt-1 text-[13px] leading-snug text-ink-soft">
            No tienes ingresos ni cortes de tarjeta a la vista. Cuando registres una tarjeta o un
            gasto fijo, sus vencimientos aparecerán aquí.
          </p>
        )}
      </div>
    </div>
  )
}

/* ── Accesos a la configuración ─────────────────────────────────────────── */

/**
 * Los dos ajustes que ya existían en la API pero no tenían puerta: qué avisos
 * llegan y con cuánta antelación, y los recordatorios que uno se pone. Sin este
 * bloque, ambos siguen siendo endpoints sin pantalla.
 */
function AccesosConfiguracion() {
  const navigate = useNavigate()
  const { data: recordatorios } = useRecordatorios()

  const activos = (recordatorios ?? []).filter((r) => r.enabled).length

  return (
    <ul className="av-filas">
      <FilaAcceso
        Icono={BellRingingIcon}
        titulo="Tipos de aviso y horarios"
        detalle="Qué te avisamos, con cuánta antelación y a qué hora."
        onClick={() => navigate('/avisos/ajustes')}
      />
      <FilaAcceso
        Icono={CalendarBlankIcon}
        titulo="Tus recordatorios"
        detalle={
          recordatorios === undefined
            ? 'Los que tú creas, con su propia fecha y hora.'
            : activos > 0
              ? `${activos} ${activos === 1 ? 'activo' : 'activos'}. Los que tú creas, con su fecha y hora.`
              : 'Todavía no tienes ninguno. Crea el primero.'
        }
        onClick={() => navigate('/avisos/ajustes?ver=recordatorios')}
      />
    </ul>
  )
}

function FilaAcceso({
  Icono,
  titulo,
  detalle,
  onClick,
}: {
  Icono: typeof BellRingingIcon
  titulo: string
  detalle: string
  onClick: () => void
}) {
  return (
    <li>
      <button
        type="button"
        onClick={() => {
          haptic('light')
          onClick()
        }}
        className="flex min-h-[56px] w-full items-center gap-3 py-3 text-left active:opacity-70"
      >
        <span className="flex size-9 shrink-0 items-center justify-center rounded-chip bg-surface-sunken text-ink-soft">
          <Icono size={17} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14px] font-semibold text-ink">{titulo}</span>
          <span className="block text-[12px] leading-snug text-ink-soft">{detalle}</span>
        </span>
        <CaretRightIcon size={15} className="shrink-0 text-ink-faint" />
      </button>
    </li>
  )
}

/* ── Marco ───────────────────────────────────────────────────────────────── */

function Encabezado({
  subtitulo,
  onVolver,
  onAjustes,
}: {
  subtitulo?: string
  onVolver: () => void
  onAjustes: () => void
}) {
  return (
    <ScreenHeader
      title="Avisos"
      subtitle={subtitulo}
      tinte="butter"
      action={
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => {
              haptic('light')
              onAjustes()
            }}
            aria-label="Ajustes de avisos"
            className="flex size-11 items-center justify-center rounded-full text-ink-soft active:opacity-60"
          >
            <SlidersHorizontalIcon size={19} />
          </button>
          <button
            type="button"
            onClick={() => {
              haptic('light')
              onVolver()
            }}
            className="flex min-h-[44px] items-center gap-1.5 rounded-pill border border-line bg-surface px-3.5 text-[13px] font-semibold text-ink active:scale-95"
          >
            <ArrowLeftIcon size={15} weight="bold" />
            Inicio
          </button>
        </div>
      }
    />
  )
}

/** Sección de cuerpo: filete, rótulo numerado y contenido. La misma voz del inicio. */
function Seccion({
  numero,
  titulo,
  extra,
  children,
}: {
  numero: string
  titulo: string
  /** Contador al margen del título, p. ej. «4 nuevas». */
  extra?: string
  children: React.ReactNode
}) {
  return (
    <section className="av-seccion">
      <header className="mb-2 flex items-baseline gap-2.5">
        <span className="av-fechado shrink-0">{numero}</span>
        <h2 className="min-w-0 flex-1 truncate font-display text-[19px] font-bold tracking-[-0.035em] text-ink">
          {titulo}
        </h2>
        {extra ? <span className="av-nuevo shrink-0">{extra}</span> : null}
      </header>
      {children}
    </section>
  )
}

function Cargando() {
  return (
    <div data-pantalla="avisos">
      <style>{CSS_AVISOS}</style>
      <ScreenHeader title="Avisos" subtitle="Buscando qué falta…" tinte="butter" />
      <div className="px-safe mx-auto flex max-w-lg flex-col gap-6 pt-2">
        {[0, 1].map((bloque) => (
          <div key={bloque} className="flex flex-col gap-3 border-t border-line pt-4">
            <Skeleton className="h-3 w-40" />
            {[0, 1, 2].map((fila) => (
              <div key={fila} className="flex items-center gap-3">
                <div className="flex flex-1 flex-col gap-1.5">
                  <Skeleton className="h-2.5 w-16" />
                  <Skeleton className="h-3.5 w-4/5" />
                  <Skeleton className="h-3 w-3/5" />
                </div>
                <Skeleton className="h-8 w-24 rounded-pill" />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

const CSS_AVISOS = `
/* La misma gramática del inicio —filete y rótulo en vez de tarjeta— pero con
   prefijo propio: las clases del inicio viven dentro de su propio ámbito y
   copiarlas por nombre las ataría a un archivo que otra persona está tocando. */
[data-pantalla='avisos'] .av-seccion {
  border-top: 1px solid var(--border);
  padding-top: 1.1rem;
  margin-top: 1.3rem;
}
[data-pantalla='avisos'] .av-seccion:first-of-type { margin-top: 0.75rem; }

[data-pantalla='avisos'] .av-versalita {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  line-height: 1.4;
}
[data-pantalla='avisos'] .av-fechado {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-faint);
}

/* Filete entre renglones, ninguno al principio ni al final: la lista se lee
   como una columna de periódico y no como una pila de cajas. */
[data-pantalla='avisos'] .av-filas > li + li { border-top: 1px solid var(--border); }
/* El filete no cruza un bloque teñido ni el que va justo debajo: una línea que
   atraviesa el fondo de color lo parte en dos y deja de leerse como un bloque. */
[data-pantalla='avisos'] .av-filas > li:has(> .bg-primary-soft),
[data-pantalla='avisos'] .av-filas > li:has(> .bg-primary-soft) + li { border-top-color: transparent; }

/* Distintivo de «nuevo». Va en la línea de estado y no como punto suelto: un
   punto más, junto al de la persona, dejaría de leerse como identidad. */
[data-pantalla='avisos'] .av-nuevo {
  display: inline-block;
  border-radius: 999px;
  background: var(--primary-soft);
  color: var(--primary-text);
  padding: 1px 6px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
`

/* ── Utilidades ─────────────────────────────────────────────────────────── */

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
const MESES_LARGOS = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
]

function diaMes(fecha: PlainDate): string {
  const [, mes, dia] = fecha.split('-')
  return `${Number(dia)} ${MESES[Number(mes) - 1]}`
}

function fechaLarga(fecha: PlainDate): string {
  const [, mes, dia] = fecha.split('-')
  return `${Number(dia)} de ${MESES_LARGOS[Number(mes) - 1]}`
}

/**
 * La línea de estado de un aviso.
 *
 * Dice **cuándo**, no qué pasa, y siempre lleva la fecha exacta: los títulos
 * del servidor dan el plazo en relativo —«vence hoy», «venció hace 5 días»— y
 * nunca el día. Es el único dato que esta línea aporta de verdad, y es el que
 * hace falta para decidir si da tiempo de pagarlo antes del fin de semana.
 */
function cuando(aviso: AvisoBandeja, hoy: PlainDate): string {
  const dias = differenceInDays(aviso.fecha, hoy)

  if (aviso.urgencia === 'VENCIDO') return `Vencido · ${diaMes(aviso.fecha)}`
  if (aviso.urgencia === 'HOY') return `Hoy · ${diaMes(aviso.fecha)}`
  if (aviso.urgencia === 'PRONTO') {
    if (dias <= 1) return `Mañana · ${diaMes(aviso.fecha)}`
    return `En ${dias} días · ${diaMes(aviso.fecha)}`
  }
  return diaMes(aviso.fecha)
}

/** La hora del día en que ocurrió algo. La fecha la pone el rótulo del grupo. */
function hora(iso: string): string {
  const fecha = new Date(iso)
  if (Number.isNaN(fecha.getTime())) return ''
  return `${String(fecha.getHours()).padStart(2, '0')}:${String(fecha.getMinutes()).padStart(2, '0')}`
}

/**
 * La actividad, partida por día.
 *
 * Sin el corte por día, treinta líneas con hora suelta obligan a comparar
 * marcas de tiempo para saber si algo fue hoy o la semana pasada. El servidor
 * ya la devuelve de más reciente a más antigua, así que basta con agrupar sin
 * reordenar — reordenar aquí sería reimplementar su criterio.
 */
function agruparPorDia(
  filas: readonly ActividadBandeja[],
  hoy: PlainDate,
): Array<{ dia: string; etiqueta: string; filas: ActividadBandeja[] }> {
  const grupos: Array<{ dia: string; etiqueta: string; filas: ActividadBandeja[] }> = []

  for (const fila of filas) {
    // Se parte del `Date` local y no de la cadena ISO: `createdAt` viene en UTC
    // y en México eso cae en el día anterior media jornada de cada día.
    const f = new Date(fila.createdAt)
    if (Number.isNaN(f.getTime())) continue
    const dia = `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, '0')}-${String(f.getDate()).padStart(2, '0')}`

    const ultimo = grupos[grupos.length - 1]
    if (ultimo && ultimo.dia === dia) {
      ultimo.filas.push(fila)
      continue
    }
    grupos.push({ dia, etiqueta: etiquetaDeDia(dia as PlainDate, hoy), filas: [fila] })
  }

  return grupos
}

function etiquetaDeDia(dia: PlainDate, hoy: PlainDate): string {
  const diferencia = differenceInDays(dia, hoy)
  if (diferencia === 0) return 'Hoy'
  if (diferencia === -1) return 'Ayer'
  return diaMes(dia)
}

/**
 * El resumen del servidor llega en mayúscula inicial —«Registró gasto de…»—
 * porque también viaja suelto en un push. Aquí va detrás del nombre de quien lo
 * hizo, así que la mayúscula partiría la frase por la mitad.
 */
function minuscula(texto: string): string {
  if (!texto) return texto
  return texto.charAt(0).toLocaleLowerCase('es-MX') + texto.slice(1)
}
